<template>
    <commercial-template :prospect-count="totalProspects">
        <div class="p-6 max-w-[1400px] mx-auto">

            <!-- Header -->
            <div class="flex items-center justify-between mb-8">
                <div>
                    <h1 class="text-2xl font-black text-gray-900 tracking-tight">
                        Bonjour, {{ $account.account.prenom }} 👋
                    </h1>
                    <p class="text-sm text-gray-500 mt-0.5">Voici un aperçu de votre activité commerciale</p>
                </div>
                <button
                    @click="showNouvelleDeMarche = true"
                    class="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-orange-500 to-red-500 text-white text-sm font-bold rounded-xl shadow-md shadow-orange-200 hover:shadow-lg hover:shadow-orange-300 transition-all duration-200 hover:-translate-y-0.5">
                    <i class="fas fa-plus text-xs"></i>
                    Nouvelle démarche
                </button>
            </div>

            <!-- Loader -->
            <div v-if="loading" class="flex items-center justify-center py-24">
                <div class="flex flex-col items-center gap-3">
                    <div class="w-10 h-10 border-3 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
                    <span class="text-sm text-gray-400 font-medium">Chargement...</span>
                </div>
            </div>

            <template v-if="!loading && stats">

                <!-- Stats grid -->
                <div class="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
                    <div
                        v-for="(cfg, key) in statusConfig"
                        :key="key"
                        @click="$router.push('/commercial/prospects')"
                        class="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer group hover:-translate-y-0.5">
                        <div class="flex items-start justify-between mb-3">
                            <div class="w-9 h-9 rounded-xl flex items-center justify-center" :class="cfg.iconBg">
                                <i :class="[cfg.icon, cfg.iconText, 'text-sm']"></i>
                            </div>
                            <span class="text-2xl font-black text-gray-900">{{ stats[key] || 0 }}</span>
                        </div>
                        <p class="text-xs font-semibold text-gray-500 uppercase tracking-wide">{{ cfg.label }}</p>
                        <div class="mt-2 h-1 rounded-full bg-gray-100 overflow-hidden">
                            <div class="h-full rounded-full transition-all duration-700" :class="cfg.bar" :style="{ width: getBarWidth(stats[key]) + '%' }"></div>
                        </div>
                    </div>
                </div>

                <!-- Content grid -->
                <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">

                    <!-- Recent prospects -->
                    <div class="lg:col-span-2 bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                        <div class="flex items-center justify-between px-6 py-4 border-b border-gray-50">
                            <h2 class="font-bold text-gray-900">Derniers prospects</h2>
                            <a href="/commercial/prospects" class="text-xs font-semibold text-orange-500 hover:text-orange-600 transition-colors">
                                Voir tout <i class="fas fa-arrow-right ml-1 text-[10px]"></i>
                            </a>
                        </div>

                        <div v-if="recent && recent.length">
                            <div
                                v-for="prospect in recent"
                                :key="prospect.id"
                                @click="$router.push('/commercial/prospects')"
                                class="flex items-center gap-4 px-6 py-4 border-b border-gray-50 last:border-0 hover:bg-gray-50/60 transition-colors cursor-pointer group">
                                <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-100 to-red-100 flex items-center justify-center font-black text-orange-600 text-sm flex-shrink-0">
                                    {{ prospect.company_name.charAt(0).toUpperCase() }}
                                </div>
                                <div class="flex-1 min-w-0">
                                    <p class="text-sm font-bold text-gray-900 truncate">{{ prospect.company_name }}</p>
                                    <p class="text-xs text-gray-400 mt-0.5">
                                        <template v-if="prospect.contacts && prospect.contacts.length">
                                            <i class="fas fa-user text-[10px] mr-1"></i>
                                            {{ prospect.contacts[0].prenom }} {{ prospect.contacts[0].nom }}
                                        </template>
                                        <template v-else>
                                            <i class="fas fa-user-slash text-[10px] mr-1"></i> Aucun contact
                                        </template>
                                    </p>
                                </div>
                                <div class="flex flex-col items-end gap-1.5">
                                    <span class="text-[10px] font-bold px-2.5 py-1 rounded-full" :class="getStatusBadge(prospect.status)">
                                        {{ statusConfig[prospect.status] ? statusConfig[prospect.status].label : prospect.status }}
                                    </span>
                                    <span class="text-[10px] text-gray-400">{{ prospect.created_at | momentDate }}</span>
                                </div>
                            </div>
                        </div>

                        <div v-else class="flex flex-col items-center justify-center py-14 text-center px-6">
                            <div class="w-14 h-14 rounded-2xl bg-orange-50 flex items-center justify-center mb-3">
                                <i class="fas fa-inbox text-orange-300 text-2xl"></i>
                            </div>
                            <p class="text-sm font-semibold text-gray-500">Aucun prospect pour l'instant</p>
                            <button @click="showNouvelleDeMarche = true" class="mt-3 text-xs font-bold text-orange-500 hover:text-orange-600">
                                Créer votre premier prospect →
                            </button>
                        </div>
                    </div>

                    <!-- Quick actions + funnel -->
                    <div class="space-y-4">
                        <!-- Funnel summary -->
                        <div class="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                            <h2 class="font-bold text-gray-900 mb-4">Entonnoir de vente</h2>
                            <div class="space-y-3">
                                <div v-for="(cfg, key) in funnelStatuses" :key="key" class="flex items-center gap-3">
                                    <div class="w-2.5 h-2.5 rounded-full flex-shrink-0" :class="cfg.dot"></div>
                                    <div class="flex-1">
                                        <div class="flex justify-between text-xs mb-1">
                                            <span class="font-medium text-gray-600">{{ cfg.label }}</span>
                                            <span class="font-bold text-gray-900">{{ stats[key] || 0 }}</span>
                                        </div>
                                        <div class="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                            <div class="h-full rounded-full transition-all duration-700" :class="cfg.bar" :style="{ width: getBarWidth(stats[key]) + '%' }"></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Quick actions -->
                        <div class="bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl p-5 text-white shadow-md shadow-orange-200">
                            <h3 class="font-bold mb-1">Prêt à convertir ?</h3>
                            <p class="text-xs text-orange-100 mb-4">{{ stats.negociation || 0 }} prospect(s) en négociation</p>
                            <a href="/commercial/prospects" class="block w-full text-center text-xs font-bold py-2.5 bg-white text-orange-600 rounded-xl hover:bg-orange-50 transition-colors">
                                Voir le kanban <i class="fas fa-arrow-right ml-1"></i>
                            </a>
                        </div>
                    </div>
                </div>
            </template>
        </div>

        <!-- Modal nouvelle démarche -->
        <nouvelle-de-marche
            v-if="showNouvelleDeMarche"
            @close="showNouvelleDeMarche = false"
            @created="onProspectCreated"
        />
    </commercial-template>
</template>

<script>
import CommercialTemplate from './CommercialTemplate.vue';
import NouvelleDeMarche from './NouvelleDeMarche.vue';
import moment from 'moment';

export default {
    name: 'CommercialDashboard',
    components: { CommercialTemplate, NouvelleDeMarche },
    data() {
        return {
            loading: true,
            stats: null,
            recent: [],
            showNouvelleDeMarche: false,
            statusConfig: {
                nouveau:     { label: 'Nouveau',     icon: 'fas fa-star',       iconBg: 'bg-gray-100',    iconText: 'text-gray-500',   bar: 'bg-gray-400',   dot: 'bg-gray-400' },
                contacte:    { label: 'Contacté',    icon: 'fas fa-phone',      iconBg: 'bg-blue-50',     iconText: 'text-blue-500',   bar: 'bg-blue-400',   dot: 'bg-blue-400' },
                en_cours:    { label: 'En cours',    icon: 'fas fa-sync-alt',   iconBg: 'bg-yellow-50',   iconText: 'text-yellow-500', bar: 'bg-yellow-400', dot: 'bg-yellow-400' },
                proposition: { label: 'Proposition', icon: 'fas fa-file-alt',   iconBg: 'bg-orange-50',   iconText: 'text-orange-500', bar: 'bg-orange-400', dot: 'bg-orange-400' },
                negociation: { label: 'Négociation', icon: 'fas fa-handshake',  iconBg: 'bg-purple-50',   iconText: 'text-purple-500', bar: 'bg-purple-400', dot: 'bg-purple-400' },
                relance:     { label: 'Relance',     icon: 'fas fa-redo',       iconBg: 'bg-red-50',      iconText: 'text-red-500',    bar: 'bg-red-400',    dot: 'bg-red-400' },
                gagne:       { label: 'Gagné',       icon: 'fas fa-trophy',     iconBg: 'bg-green-50',    iconText: 'text-green-500',  bar: 'bg-green-400',  dot: 'bg-green-400' },
                perdu:       { label: 'Perdu',       icon: 'fas fa-times',      iconBg: 'bg-slate-100',   iconText: 'text-slate-400',  bar: 'bg-slate-300',  dot: 'bg-slate-400' },
            },
        };
    },
    computed: {
        totalProspects() {
            if (!this.stats) return null;
            return Object.entries(this.stats)
                .filter(([k]) => !['gagne', 'perdu'].includes(k))
                .reduce((a, [, v]) => a + v, 0) || null;
        },
        funnelStatuses() {
            const keys = ['nouveau', 'contacte', 'en_cours', 'proposition', 'negociation', 'relance'];
            return Object.fromEntries(keys.map(k => [k, this.statusConfig[k]]));
        }
    },
    filters: {
        momentDate(v) { return v ? moment(v).format('DD/MM/YYYY') : '-'; }
    },
    methods: {
        async fetchDashboard() {
            this.loading = true;
            const res = await this.$request.get('/commercial/dashboard');
            if (res) {
                this.stats = res.stats;
                this.recent = res.recent;
            }
            this.loading = false;
        },
        getBarWidth(val) {
            if (!this.stats || !val) return 0;
            const max = Math.max(...Object.values(this.stats), 1);
            return Math.round((val / max) * 100);
        },
        getStatusBadge(status) {
            const map = {
                nouveau:     'bg-gray-100 text-gray-600',
                contacte:    'bg-blue-100 text-blue-700',
                en_cours:    'bg-yellow-100 text-yellow-700',
                proposition: 'bg-orange-100 text-orange-700',
                negociation: 'bg-purple-100 text-purple-700',
                relance:     'bg-red-100 text-red-700',
                gagne:       'bg-green-100 text-green-700',
                perdu:       'bg-slate-100 text-slate-600',
            };
            return map[status] || 'bg-gray-100 text-gray-600';
        },
        onProspectCreated() {
            this.showNouvelleDeMarche = false;
            this.fetchDashboard();
        }
    },
    mounted() {
        this.fetchDashboard();
    }
};
</script>